/**
 * Centralized route configuration for the CRM application.
 * Single source of truth for paths, permissions, and route definitions.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import Login from '../components/Login/Login';
import Register from '../components/Register';
import Dashboard from '../pages/Dashboard.jsx';
import CreateMerchant from '../pages/CreateMerchant';
import InUnderwriting from '../pages/InUnderwriting';
import Pending from '../pages/Pending';
import Complete from '../pages/Complete';
import ViewMerchant from '../pages/ViewMerchant';
import ProfilePage from '../pages/ProfilePage.jsx';
import UserCreatePage from '../pages/UserCreatePage.jsx';
import UserListPage from '../pages/UserListPage.jsx';
import TeamsListPage from '../pages/TeamsListPage.jsx';
import CreateTeamPage from '../pages/CreateTeamPage.jsx';
import ViewLogsPage from '../pages/ViewLogsPage.jsx';
import LeadList from '../pages/Leads/LeadList.jsx';
import CreateLead from '../pages/Leads/CreateLead.jsx';
import MeetingCalendar from '../pages/meetings/MeetingCalendar.jsx';
import MeetingView from '../pages/meetings/MeetingView.jsx';
import CreateMeeting from '../pages/meetings/CreateMeeting.jsx';
import EditMeeting from '../pages/meetings/EditMeeting.jsx';
import SalesDbaListing from '../pages/SalesDbaListing.jsx';
import LeadDetail from '../pages/Leads/LeadDetail.jsx';
import PermissionAssignment from '../pages/permission/PermissionAssignment.jsx';
import RolesManagement from '../pages/permission/RolesManagement.jsx';
import IssueList from '../pages/Issues/IssueList.jsx';
import IssueDetail from '../pages/Issues/IssueDetail.jsx';
import MerchantList from '../pages/Merchants/MerchantsList.jsx';
import Equipemnts from '../pages/Merchants/Equipemnts.jsx';
import ContractList from '../pages/contracts/ContractList.jsx';

/** Wraps a component with ProtectedRoute */
const Protected = ({ children, permission, permissions = [] }) => (
  <ProtectedRoute
    requiredPermission={permissions.length > 0 ? undefined : permission}
    requiredPermissions={permissions.length > 0 ? permissions : []}
  >
    {children}
  </ProtectedRoute>
);

/**
 * All application routes. Add new routes here to keep App.js lean.
 */
export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/register" element={<Register />} />
    <Route path="/" element={<Protected><Navigate to="/dashboard" replace /></Protected>} />

    <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
    <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />

    {/* Merchant / DBA routes */}
    <Route path="/create-merchant" element={<Protected permission="dba-create"><CreateMerchant /></Protected>} />
    <Route path="/in-underwriting" element={<Protected permission="dba-list"><InUnderwriting /></Protected>} />

    <Route path="/merchants/accounts" element={<Protected permission="dba-list"><MerchantList /></Protected>} />
    <Route path="/merchants/equipments" element={<Protected permission="dba-list"><Equipemnts /></Protected>} />


    <Route path="/pending" element={<Protected permission="dba-pending-list"><Pending /></Protected>} />
    <Route path="/complete" element={<Protected permission="dba-completed-list"><Complete /></Protected>} />
    <Route path="/view-merchant" element={<Protected permission="dba-view"><ViewMerchant /></Protected>} />
    <Route path="/sales-dba" element={<Protected><SalesDbaListing /></Protected>} />

    {/* User management */}
    <Route path="/create-user" element={<Protected permission="user-create"><UserCreatePage /></Protected>} />
    <Route path="/users-list" element={<Protected permissions={['user-list', 'user-direct-permission']}><UserListPage /></Protected>} />

    {/* Team management */}
    <Route path="/teams-lists" element={<Protected permission="team-list"><TeamsListPage /></Protected>} />
    <Route path="/create-team" element={<Protected permission="team-create"><CreateTeamPage /></Protected>} />

    {/* Leads */}
    <Route path="/leads" element={<Protected permission="lead-list"><LeadList /></Protected>} />
    <Route path="/leads/create" element={<Protected permission="lead-create"><CreateLead /></Protected>} />
    <Route path="/leads/edit/:id" element={<Protected permission="lead-edit"><CreateLead /></Protected>} />
    <Route path="/leads/view/:id" element={<Protected permission="lead-view"><LeadDetail /></Protected>} />

    {/* Issues */}
    <Route path="/issues" element={<Protected permission="issue-list"><IssueList /></Protected>} />
    <Route path="/issues/view/:id" element={<Protected permission="issue-view"><IssueDetail /></Protected>} />

    {/* Permissions - either permission grants access */}
    <Route path="/permission/role" element={<Protected permission="role-view"><RolesManagement /></Protected>} />
    <Route path="/permissions/assign" element={<Protected permissions={['role-view', 'user-direct-permission']}><PermissionAssignment /></Protected>} />

    {/* Contracts */}
    <Route path="/contracts" element={<Protected><ContractList /></Protected>} />

    {/* Other */}
    <Route path="/view-log/:id" element={<Protected permission="log-activity-view"><ViewLogsPage /></Protected>} />
    <Route path="/meetings/calendar" element={<Protected><MeetingCalendar /></Protected>} />
    <Route path="/meetings/create" element={<Protected><CreateMeeting /></Protected>} />
    <Route path="/meetings/edit/:id" element={<Protected><EditMeeting /></Protected>} />
    <Route path="/meetings/view/:id" element={<Protected><MeetingView /></Protected>} />

    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);
